import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Runs an async loader and tracks { data, loading, error }.
 * Re-runs when `deps` change; `reload()` re-runs on demand.
 */
export default function useFetch(loader, deps = [], { initial = null, skip = false } = {}) {
  const [state, setState] = useState({ data: initial, loading: !skip, error: null });
  const seq = useRef(0);

  const run = useCallback(() => {
    if (skip) return Promise.resolve();
    const id = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    return loader()
      .then((data) => {
        if (id === seq.current) setState({ data, loading: false, error: null });
        return data;
      })
      .catch((error) => {
        if (id === seq.current) setState((s) => ({ ...s, loading: false, error }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, skip]);

  useEffect(() => {
    run();
  }, [run]);

  const setData = useCallback((updater) => {
    setState((s) => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  }, []);

  return { ...state, reload: run, setData };
}
